import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';

import { globalConst, isEmptyArr } from 'modules/global';

import { IOlympicResults, IRequiredSubjectStat } from '../models';

interface IProps {
  results?: IOlympicResults[];
}

export default function ResultsTable({ results }: IProps) {
  const t = useTranslations();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFixedRow, setShowFixedRow] = useState(true);
  const userResult = useMemo(() => results?.find(u => u?.type === 'self'), [results]);

  const calculateTotalScore = (stats: IRequiredSubjectStat[], field: 'obtained_ball' | 'max_ball') =>
    stats?.reduce((sum, subj) => sum + subj[field], 0) || 0;

  useEffect(() => {
    const container = scrollRef.current;

    if (!container) return;

    const handleScroll = () => {
      const userRow = container.querySelector(`tr[data-id="${userResult?.id}"]`);

      if (!userRow) return;

      const rowRect = userRow.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const isVisible = rowRect.top >= containerRect.top && rowRect.bottom <= containerRect.bottom;

      setShowFixedRow(!isVisible);
    };

    container.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => container.removeEventListener('scroll', handleScroll);
  }, [userResult]);

  const subjects = results?.[0]?.subject_stats || [];
  const requiredSubjects = results?.[0]?.required_subject_stats || [];

  return (
    <div ref={scrollRef} className="max-h-170 relative relative mt-5 overflow-y-scroll">
      <div className=" ">
        <table className="min-w-full table-auto">
          <thead className={`sticky top-0 z-10 rounded-t-xl text-xs sm:text-base bg-[${globalConst.colors.bgGray}]`}>
            <tr>
              <th className="w-25 rounded-tl-xl p-2 text-center font-medium">{t('lavozim')}</th>
              <th className="w-63 p-2 text-left font-medium">{t('ishtirokchi')}</th>
              {subjects.map((subject, index) => (
                <th
                  key={subject?.subject_id}
                  className="sm:w-30 md:w-45 hidden p-2 text-left font-medium md:table-cell"
                >
                  {index + 1}-{t('Fan').toLowerCase()}
                </th>
              ))}
              {!isEmptyArr(requiredSubjects) && (
                <th className="md:w-55 hidden p-2 text-left font-medium sm:w-48 md:table-cell">
                  {t('majburiy_fanlar')}
                </th>
              )}
              <th className="p-2 text-left font-medium">{t('ball')}</th>
              <th className="rounded-tr-xl p-2 text-right font-medium">{t('yakunlandi')}</th>
            </tr>
          </thead>
          <tbody>
            {results?.map(result => (
              <tr
                key={result.id}
                data-id={result.id}
                className={`${result.type === 'self' ? 'bg-blue-100 font-semibold' : ''} border-b border-gray-300 text-xs sm:text-base`}
              >
                <td className="p-2 text-center">
                  {result?.position}-{t('orin')}
                </td>
                <td className="p-2">
                  {result?.profile_name} <p className="text-sm text-gray-400">{result?.phone}</p>
                </td>
                {result.subject_stats.map(subject => (
                  <td key={subject?.subject_id} className="hidden p-2 md:table-cell">
                    {subject.obtained_ball}/{subject.max_ball}
                    <p className="text-sm text-gray-400">{subject.subject_name}</p>
                  </td>
                ))}
                {!isEmptyArr(requiredSubjects) && (
                  <td className="hidden p-2 md:table-cell">
                    {calculateTotalScore(result?.required_subject_stats, 'obtained_ball')}/
                    {calculateTotalScore(result?.required_subject_stats, 'max_ball')}
                    <p className="text-sm text-gray-400">{t('barchasi')}</p>
                  </td>
                )}
                <td className="p-2">
                  {result?.total_score}/{result?.accuracy_percentage}%{' '}
                  <p className="text-sm text-gray-400">{t('jami')}</p>
                </td>
                <td className="p-2 text-right">
                  {result?.finished_at ? (
                    <>
                      {dayjs(result?.finished_at).format('HH:mm')}{' '}
                      <p className="text-sm text-gray-400">{dayjs(result?.finished_at).format('DD-MMMM')}</p>
                    </>
                  ) : (
                    '--'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {userResult && userResult?.position > 10 && (
        <div
          className={classNames(
            'sticky bottom-0 left-0 z-30 w-full transform rounded-xl bg-gray-800 p-2 transition-all duration-500 ease-in-out sm:p-3',
            showFixedRow ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'
          )}
        >
          <h4 className="text-sm text-white sm:text-base">{t('sizning_natijangiz')}:</h4>

          <div className="mx-auto mt-2 max-w-7xl overflow-x-auto rounded-lg">
            <table className="min-w-full table-fixed text-xs sm:text-base">
              <tbody>
                <tr className="bg-white">
                  <td className="sm:w-25 p-1 text-center sm:p-2">
                    {userResult?.position}-{t('orin')}
                  </td>
                  <td className="p-1 sm:w-60 sm:p-2">
                    {userResult?.profile_name}
                    <p className="text-sm text-gray-400">{userResult?.phone}</p>
                  </td>
                  {userResult.subject_stats.map(self => (
                    <td key={self?.subject_id} className="w-45 hidden p-1 sm:p-2 md:table-cell">
                      {self.obtained_ball}/{self.max_ball}
                      <p className="text-sm text-gray-400">{self.subject_name}</p>
                    </td>
                  ))}
                  {!isEmptyArr(requiredSubjects) && (
                    <td className="w-55 hidden p-1 sm:p-2 md:table-cell">
                      {calculateTotalScore(userResult?.required_subject_stats, 'obtained_ball')}/
                      {calculateTotalScore(userResult?.required_subject_stats, 'max_ball')}{' '}
                      <p className="text-sm text-gray-400">{t('barchasi')}</p>
                    </td>
                  )}
                  <td className="p-1 sm:p-2">
                    {userResult?.total_score}/{userResult?.accuracy_percentage}
                    <p className="text-sm text-gray-400">{t('jami')}</p>
                  </td>
                  <td className="p-1 text-right sm:p-2">
                    {userResult?.finished_at ? (
                      <>
                        {dayjs(userResult?.finished_at).format('HH:mm')}{' '}
                        <p className="text-sm text-gray-400">{dayjs(userResult?.finished_at).format('DD-MMMM')}</p>
                      </>
                    ) : (
                      '--'
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
